/**
 * Generic experiments framework: deterministic bucketing and assignment
 * analytics. Individual experiments live in ./experiments/ and supply a
 * config of the shape:
 *
 *   {
 *     key: string,    // short identifier used for hash bucketing
 *     name: string,   // human-readable identifier used in analytics events
 *     variations: [{ name: string, weight: number }]
 *   }
 *
 * Variation weights must sum to 100.
 */
const { sendGenericEvent } = require("@wattpad/client-platform-web-events");

/**
 * FNV-1a 32-bit hash.
 * Deterministic and fast - suitable for bucket assignment from a string key.
 * Source: https://en.wikipedia.org/wiki/Fowler–Noll–Vo_hash_function
 */
function fnv1a(str) {
  let hash = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    hash ^= str.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

function getBucket(uuid, experimentKey) {
  return fnv1a(uuid + experimentKey) % 100;
}

function getVariation(experiment, uuid) {
  const bucket = getBucket(uuid, experiment.key);
  let cumulative = 0;
  for (const v of experiment.variations) {
    cumulative += v.weight;
    if (bucket < cumulative) return v.name;
  }
  return experiment.variations[experiment.variations.length - 1].name;
}

/**
 * Fire a once-per-session analytics event recording which bucket a user was
 * assigned to. The tracker already attaches the uuid to all events, so downstream
 * queries can join any future events back to this assignment. The "already sent"
 * flag is namespaced per experiment key so multiple experiments don't collide,
 * and lives in sessionStorage so the event re-fires on the next fresh visit.
 */
function sendBucketEvent(experiment, variation) {
  const sentKey = `bucket_event_sent_${experiment.key}`;
  try {
    if (sessionStorage.getItem(sentKey)) return;

    sendGenericEvent({
      name: "experiment_assignment",
      field1: experiment.name,
      field2: variation
    });
    sessionStorage.setItem(sentKey, "true");
  } catch (e) {
    // noop - sessionStorage may be unavailable in private browsing
  }
}

module.exports = {
  fnv1a,
  getBucket,
  getVariation,
  sendBucketEvent
};
